"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var GridSelection_1 = require("./GridSelection");
var Utils_1 = require("./Utils");
var CellPointerEventBehavior;
(function (CellPointerEventBehavior) {
    CellPointerEventBehavior[CellPointerEventBehavior["SelectCell"] = 0] = "SelectCell";
    CellPointerEventBehavior[CellPointerEventBehavior["SelectRow"] = 1] = "SelectRow";
    CellPointerEventBehavior[CellPointerEventBehavior["None"] = 2] = "None";
})(CellPointerEventBehavior = exports.CellPointerEventBehavior || (exports.CellPointerEventBehavior = {}));
var RowPointerEventBehavior;
(function (RowPointerEventBehavior) {
    RowPointerEventBehavior[RowPointerEventBehavior["SelectRow"] = 0] = "SelectRow";
    RowPointerEventBehavior[RowPointerEventBehavior["None"] = 1] = "None";
})(RowPointerEventBehavior = exports.RowPointerEventBehavior || (exports.RowPointerEventBehavior = {}));
var ColumnPointerEventBehavior;
(function (ColumnPointerEventBehavior) {
    ColumnPointerEventBehavior[ColumnPointerEventBehavior["SelectColumn"] = 0] = "SelectColumn";
    ColumnPointerEventBehavior[ColumnPointerEventBehavior["None"] = 1] = "None";
})(ColumnPointerEventBehavior = exports.ColumnPointerEventBehavior || (exports.ColumnPointerEventBehavior = {}));
/**
 * The GridSelectionHandler serves as an interface between a component and the gridSelection. It takes in the
 * component and manages its indexBasedSelectionData state. It provides a method that merges the event handlers
 * from DataGrid.Props with the configurable selection actions and sets the selectionData prop from the component's
 * indexBasedSelectionData state. The selection actions update the component's indexBasedSelectionData appropriately.
 */
var GridSelectionHandler = /** @class */ (function () {
    function GridSelectionHandler(component, selection) {
        var _this = this;
        // We bind the merged pointer event handlers so it doesn't create a new method with each render
        this.boundMergedOnCellClick = 
        // tslint:disable-next-line:no-any
        function (rowData, columnKey, cell, e) {
            return _this.mergedOnCellClick(rowData, columnKey, cell, e);
        };
        this.boundMergedOnRowClick = 
        // tslint:disable-next-line:no-any
        function (rowData, rowIdx, e) {
            return _this.mergedOnRowClick(rowData, rowIdx, e);
        };
        this.boundMergedOnColumnHeaderClick = 
        // tslint:disable-next-line:no-any
        function (columnInfo, e) {
            return _this.mergedOnColumnHeaderClick(columnInfo, e);
        };
        this.component = component;
        this.gridSelection = selection || new GridSelection_1.GridSelection();
        this.cellPointerEventBehavior = CellPointerEventBehavior.SelectCell;
        this.rowPointerEventBehavior = RowPointerEventBehavior.SelectRow;
        this.columnPointerEventBehavior = ColumnPointerEventBehavior.SelectColumn;
        this.useRangedSelection = true;
    }
    /**
     * Configuration setters and getters
     */
    GridSelectionHandler.prototype.setCellPointerEventBehavior = function (cellSelectionBehavior) {
        this.cellPointerEventBehavior = cellSelectionBehavior;
    };
    GridSelectionHandler.prototype.getCellPointerEventBehavior = function () {
        return this.cellPointerEventBehavior;
    };
    GridSelectionHandler.prototype.setRowPointerEventBehavior = function (rowPointerEventBehavior) {
        this.rowPointerEventBehavior = rowPointerEventBehavior;
    };
    GridSelectionHandler.prototype.getRowPointerEventBehavior = function () {
        return this.rowPointerEventBehavior;
    };
    GridSelectionHandler.prototype.setColumnPointerEventBehavior = function (columnPointerEventBehavior) {
        this.columnPointerEventBehavior = columnPointerEventBehavior;
    };
    GridSelectionHandler.prototype.getColumnPointerEventBehavior = function () {
        return this.columnPointerEventBehavior;
    };
    GridSelectionHandler.prototype.shouldUseRangedSelection = function (useRangedSelection) {
        this.useRangedSelection = useRangedSelection;
    };
    /**
     * Other
     */
    GridSelectionHandler.prototype.clearSelectionAndResetAnchorPoint = function () {
        this.gridSelection.clearSelection();
        this.gridSelection.resetAnchorPoint();
        this.updateComponentSelectionState();
    };
    GridSelectionHandler.prototype.getSelection = function () {
        return this.generateNewSelectionData();
    };
    GridSelectionHandler.prototype.initializeSelection = function (selectionData) {
        this.gridSelection.initializeSelection(selectionData);
    };
    /**
     * This method takes in DataGrid.Props and merges its pointer event handlers with selection actions.
     * It also adds the selectionData as the component's indexBasedSelectionData state which is modified
     * by the selection actions.
     */
    GridSelectionHandler.prototype.mergeSelectionProps = function (originalProps) {
        this.storeOriginalEvents(originalProps);
        var newProps = {
            onCellClick: this.boundMergedOnCellClick,
            onRowClick: this.boundMergedOnRowClick,
            onColumnHeaderClick: this.boundMergedOnColumnHeaderClick,
            selectionData: this.component.state ? this.component.state.indexBasedSelectionData : undefined
        };
        return Utils_1.assign({}, originalProps, newProps);
    };
    /**
     * We store the original events so that the merged selection events can reference them
     */
    GridSelectionHandler.prototype.storeOriginalEvents = function (originalProps) {
        this.originalOnCellClick = originalProps.onCellClick;
        this.originalOnRowClick = originalProps.onRowClick;
        this.originalOnColumnHeaderClick = originalProps.onColumnHeaderClick;
    };
    /**
     * This is the handler which wraps the original onCellClick handler. It first applies cell selection and then executes
     * the original onCellClick handler.
     */
    GridSelectionHandler.prototype.mergedOnCellClick = function (
        // tslint:disable-next-line:no-any
        rowData, columnKey, cell, e) {
        if (this.cellPointerEventBehavior !== CellPointerEventBehavior.None) {
            this.cellPointerEvent(cell, e);
        }
        if (!!this.originalOnCellClick) {
            this.originalOnCellClick(rowData, columnKey, cell, e);
        }
    };
    /**
     * This is the handler which wraps the original onRowClick handler. It first applies row selection and then executes
     * the original onRowClick handler.
     */
    // tslint:disable-next-line:no-any
    GridSelectionHandler.prototype.mergedOnRowClick = function (rowData, rowIdx, e) {
        if (this.rowPointerEventBehavior !== RowPointerEventBehavior.None) {
            this.rowPointerEvent(rowIdx, e);
        }
        if (!!this.originalOnRowClick) {
            this.originalOnRowClick(rowData, rowIdx, e);
        }
    };
    /**
     * This is the handler which wraps the original onColumnHeaderClick handler. It first applies column selection and
     * then executes the original onColumnHeaderClick handler.
     */
    // tslint:disable-next-line:no-any
    GridSelectionHandler.prototype.mergedOnColumnHeaderClick = function (columnInfo, e) {
        if (this.columnPointerEventBehavior !== ColumnPointerEventBehavior.None) {
            this.columnPointerEvent(columnInfo.columnIndex, e);
        }
        if (!!this.originalOnColumnHeaderClick) {
            this.originalOnColumnHeaderClick(columnInfo, e);
        }
    };
    GridSelectionHandler.prototype.cellPointerEvent = function (cell, e) {
        switch (this.cellPointerEventBehavior) {
            case CellPointerEventBehavior.SelectCell:
                this.selectCell(cell, e);
                break;
            case CellPointerEventBehavior.SelectRow:
                this.selectRowFromEvent(cell.row, e);
                break;
            case CellPointerEventBehavior.None:
            default:
                return;
        }
        this.updateComponentSelectionState();
    };
    GridSelectionHandler.prototype.rowPointerEvent = function (row, e) {
        if (this.rowPointerEventBehavior !== RowPointerEventBehavior.SelectRow) {
            return;
        }
        this.selectRowFromEvent(row, e);
        this.updateComponentSelectionState();
    };
    GridSelectionHandler.prototype.columnPointerEvent = function (column, e) {
        if (this.columnPointerEventBehavior !== ColumnPointerEventBehavior.SelectColumn) {
            return;
        }
        this.selectColumnFromEvent(column, e);
        this.updateComponentSelectionState();
    };
    GridSelectionHandler.prototype.updateComponentSelectionState = function () {
        this.component.setState(this.generateNewState());
    };
    GridSelectionHandler.prototype.generateNewState = function () {
        // tslint:disable-next-line:no-any
        var newState = {
            indexBasedSelectionData: this.generateNewSelectionData()
        };
        return newState;
    };
    /**
     * We copy the grid selection into a new object to ensure that the updated SelectionData is rendered through
     * PureRender component.
     */
    GridSelectionHandler.prototype.generateNewSelectionData = function () {
        var originalGridSelection = this.gridSelection.getSelection();
        return {
            rowRanges: originalGridSelection.rowRanges,
            colRanges: originalGridSelection.colRanges,
            cellRanges: originalGridSelection.cellRanges
        };
    };
    GridSelectionHandler.prototype.selectCell = function (cell, e) {
        if (e.ctrlKey || e.metaKey) {
            if (GridSelection_1.GridSelection.isCellSelected(cell, this.gridSelection.getSelection())) {
                this.gridSelection.deselectCell(cell);
            }
            else {
                this.gridSelection.inclusiveCellSelect(cell);
            }
        }
        else if (this.useRangedSelection && e.shiftKey) {
            this.gridSelection.rangeCellSelectFromAnchor(cell);
        }
        else {
            this.gridSelection.exclusiveCellSelect(cell);
        }
    };
    GridSelectionHandler.prototype.exclusiveRowsSelect = function (rows) {
        this.gridSelection.exclusiveRowsSelect(rows);
        this.updateComponentSelectionState();
    };
    GridSelectionHandler.prototype.exclusiveColumnSelect = function (col) {
        this.gridSelection.exclusiveColSelect(col);
        this.updateComponentSelectionState();
    };
    GridSelectionHandler.prototype.selectRowFromEvent = function (row, e) {
        if (e.ctrlKey || e.metaKey) {
            if (GridSelection_1.GridSelection.isRowSelected(row, this.gridSelection.getSelection())) {
                this.gridSelection.deselectRow(row);
            }
            else {
                this.gridSelection.inclusiveRowSelect(row);
            }
        }
        else if (this.useRangedSelection && e.shiftKey) {
            this.gridSelection.rangeRowSelectFromAnchor(row);
        }
        else {
            this.gridSelection.exclusiveRowSelect(row);
        }
    };
    GridSelectionHandler.prototype.selectColumnFromEvent = function (column, e) {
        if (e.ctrlKey || e.metaKey) {
            if (GridSelection_1.GridSelection.isColSelected(column, this.gridSelection.getSelection())) {
                this.gridSelection.deselectColumn(column);
            }
            else {
                this.gridSelection.inclusiveColSelect(column);
            }
        }
        else if (this.useRangedSelection && e.shiftKey) {
            this.gridSelection.rangeColSelectFromAnchor(column);
        }
        else {
            this.gridSelection.exclusiveColSelect(column);
        }
    };
    GridSelectionHandler.prototype.selectRowAndColRange = function (rowRange, colRange) {
        this.gridSelection.selectRowAndColRange(rowRange, colRange);
        this.updateComponentSelectionState();
    };
    return GridSelectionHandler;
}());
exports.GridSelectionHandler = GridSelectionHandler;
//# sourceMappingURL=GridSelectionHandler.js.map